import {
  Flex,
  MenuItem,
  Tag,
  Text,
  Menu,
  MenuButton,
  IconButton,
  Portal,
  MenuList,
} from '@chakra-ui/react';
import {
  productApprovalsApi,
  ProductApprovalState,
} from '@texas/api/endpoints/productApprovalsApi';
import { useTranslation } from 'react-i18next';
import { Icons, defaultIconSize } from '../Icons';
import { productApprovalEvents } from '../productApproval/events';
import { sampleEvents } from './events';
import { SetSamplesAsLostModal } from './SetSamplesAsLostModal';
import { StopFollowSamplesModal } from './StopFollowSamplesModal';
import { SampleTrackingMenuItems } from './SampleTrackingMenuItems';
import { SampleAction, SampleLifecycle } from './types';
import { RejectModal } from '@texas/components/article/productApproval/changeStateModals/RejectModal';
import { InitiateSampling } from './actions/InitiateSampling';
import { SendToBrandOrHandlePa } from './actions/SendToBrandOrHandlePa';
import { UnpackShipment } from './actions/UnpackShipment';
import { SentToBrandHandlePa } from './actions/SentToBrandHandlePa';
import { MarkReceivedFromSupplier } from './actions/MarkReceivedFromSupplier';
import { UpdateTransportRequestWithoutShipment } from './actions/UpdateTransportRequestWithoutShipment';
import { compareEnum } from '@texas/utils/helpers/enumHelpers';
import { UpdateOwner } from './UpdateOwner';
import { useMemo } from 'react';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { usePortalRefValue } from '@texas/hooks/usePortalRef';

export interface SampleNextActionProps {
  action: SampleAction;
  lifecycle: SampleLifecycle;
  pa: { id: number; state: ProductApprovalState } | undefined;
  sampleId: number | undefined;
  inPossessionOfUserId?: number;
  groupId: number;
  variantId: number;
  branchId: number;
  supplierId: number;
  articleFolderId: number;
  shipmentId: number | undefined;
  transportRequestId: number | undefined;
  hideStopFollowing?: boolean;
}

export function SampleNextAction(props: SampleNextActionProps) {
  const hideStopFollowing = props.hideStopFollowing;

  if (!props.sampleId) {
    return <InitiateSampling {...props} />;
  }

  switch (props.action) {
    case SampleAction.RestartSampling: {
      return <InitiateSampling {...props} />;
    }
    case SampleAction.InitiateSampling: {
      return <InitiateSampling {...props} />;
    }
    case SampleAction.None: {
      return (
        <GeneralSampleMenu
          pa={props.pa!}
          sampleId={props.sampleId}
          hideStopFollowing={hideStopFollowing}
        />
      );
    }
    case SampleAction.SendToInternalOrHandlePa: {
      return (
        <SendToBrandOrHandlePa
          {...props}
          hideStopFollowing={hideStopFollowing}
        />
      );
    }
    case SampleAction.ReadySampleDatePassedSetNew: {
      return (
        <MarkReceivedFromSupplier
          {...props}
          hideStopFollowing={hideStopFollowing}
        />
      );
    }
    case SampleAction.MarkSampleReceivedFromSupplier: {
      return (
        <MarkReceivedFromSupplier
          {...props}
          hideStopFollowing={hideStopFollowing}
        />
      );
    }
    case SampleAction.SendSampleOrHandlePa: {
      return (
        <SendToBrandOrHandlePa
          {...props}
          hideStopFollowing={hideStopFollowing}
        />
      );
    }
    case SampleAction.UnpackShipment: {
      return (
        <UnpackShipment {...props} hideStopFollowing={hideStopFollowing} />
      );
    }
    case SampleAction.SentToCustomerHandlePa: {
      return (
        <SentToBrandHandlePa {...props} hideStopFollowing={hideStopFollowing} />
      );
    }
    case SampleAction.UpdateTransportRequestWithoutShipment: {
      return (
        <UpdateTransportRequestWithoutShipment
          {...props}
          hideStopFollowing={hideStopFollowing}
        />
      );
    }
  }
}

export function GeneralSampleMenu({
  pa,
  sampleId,
  hideStopFollowing,
}: {
  pa: { id: number; state: ProductApprovalState };
  sampleId: number;
  hideStopFollowing?: boolean;
}) {
  const { t } = useTranslation();
  const {
    setLostOnOpen,
    updateOwnerOnOpen,
    stopFollowOnOpen,
    rejectOnOpen,
    modals,
  } = useGeneralSampleModals();
  const { ref } = usePortalRefValue();

  return (
    <>
      <Menu isLazy={true}>
        <MenuButton
          as={IconButton}
          size="sm"
          variant="ghost"
          aria-label={t('sample.menu')}
          icon={<Icons.DotsHorizontal boxSize={defaultIconSize} />}
        />
        <Portal containerRef={ref}>
          <MenuList zIndex={10}>
            <GeneralSamplesMenuActions
              pas={[pa]}
              sampleIds={[sampleId]}
              rejectOnOpen={rejectOnOpen}
              setLostOnOpen={setLostOnOpen}
              stopFollowOnOpen={hideStopFollowing ? null : stopFollowOnOpen}
              updateOwnerOnOpen={updateOwnerOnOpen}
            />
          </MenuList>
        </Portal>
      </Menu>
      {modals}
    </>
  );
}

export function GeneralSamplesMenuActions({
  pas,
  sampleIds,
  updateOwnerOnOpen,
  setLostOnOpen,
  stopFollowOnOpen,
  rejectOnOpen,
  onPerformedAction,
}: {
  pas: { id: number; state: ProductApprovalState }[];
  sampleIds: number[];
  updateOwnerOnOpen: (ids: number[]) => void;
  setLostOnOpen: (ids: number[]) => void;
  stopFollowOnOpen: ((ids: number[]) => void) | null;
  rejectOnOpen: (id: number) => void;
  onPerformedAction?: () => void;
}) {
  const { t } = useTranslation();

  const anyRender = pas.length > 0 || sampleIds.length > 0;

  if (!anyRender) return null;

  return (
    <>
      {pas.length > 0 && (
        <>
          <MenuItem
            {...(pas.length > 1
              ? {
                  onClick: () => {
                    window.open(
                      productApprovalsApi.downloadMultipleSupplierProductApprovals(
                        pas.map((x) => x.id),
                      ),
                    );
                    if (onPerformedAction) onPerformedAction();
                  },
                }
              : {
                  as: 'a',
                  target: '_blank',
                  download: true,
                  href: productApprovalsApi.downloadSupplierProductApproval(
                    pas[0].id,
                  ),
                })}
            icon={<Icons.Download boxSize={defaultIconSize} />}
          >
            <Flex gap={1} alignItems="center">
              <Tag>{pas.length}</Tag>
              <Text>{t('productApproval.downloadSpa')}</Text>
            </Flex>
          </MenuItem>
          <MenuItem
            {...(pas.length > 1
              ? {
                  onClick: () => {
                    window.open(
                      productApprovalsApi.downloadMultipleCustomerProductApprovals(
                        false,
                        pas.map((x) => x.id),
                      ),
                    );
                    if (onPerformedAction) onPerformedAction();
                  },
                }
              : {
                  as: 'a',
                  target: '_blank',
                  download: true,
                  href: productApprovalsApi.downloadCustomerProductApproval(
                    pas[0].id,
                    false,
                  ),
                })}
            icon={<Icons.Download boxSize={defaultIconSize} />}
          >
            <Flex gap={1} alignItems="center">
              <Tag>{pas.length}</Tag>
              <Text>{t('productApproval.downloadPaWithMaterials')}</Text>
            </Flex>
          </MenuItem>
          <MenuItem
            {...(pas.length > 1
              ? {
                  onClick: () => {
                    window.open(
                      productApprovalsApi.downloadMultipleCustomerProductApprovals(
                        true,
                        pas.map((x) => x.id),
                      ),
                    );
                    if (onPerformedAction) onPerformedAction();
                  },
                }
              : {
                  as: 'a',
                  target: '_blank',
                  download: true,
                  href: productApprovalsApi.downloadCustomerProductApproval(
                    pas[0].id,
                    true,
                  ),
                })}
            icon={<Icons.Download boxSize={defaultIconSize} />}
          >
            <Flex gap={1} alignItems="center">
              <Tag>{pas.length}</Tag>
              <Text>{t('productApproval.downloadPaWithoutMaterials')}</Text>
            </Flex>
          </MenuItem>
        </>
      )}

      {sampleIds.length > 0 && (
        <>
          <MenuItem
            onClick={() => updateOwnerOnOpen(sampleIds)}
            icon={<Icons.AccountCircle boxSize={defaultIconSize} />}
          >
            <Flex gap={1} alignItems="center">
              <Tag>{sampleIds.length}</Tag>
              <Text>{t('sample.changeOwner')}</Text>
            </Flex>
          </MenuItem>
          <MenuItem
            color="red.200"
            onClick={() => setLostOnOpen(sampleIds)}
            icon={<Icons.CircleOpacity boxSize={defaultIconSize} />}
          >
            <Flex gap={1} alignItems="center">
              <Tag>{sampleIds.length}</Tag>
              <Text>{t('sample.markLost')}</Text>
            </Flex>
          </MenuItem>
          <SampleTrackingMenuItems
            sampleIds={sampleIds}
            onStopFollowing={stopFollowOnOpen}
          />
        </>
      )}
      {pas.length === 1 &&
        !compareEnum(
          ProductApprovalState,
          pas[0].state,
          ProductApprovalState.Rejected,
        ) && (
          <>
            <MenuItem
              color="red.200"
              onClick={() => rejectOnOpen(pas[0].id)}
              icon={<Icons.HandBackLeft boxSize={defaultIconSize} />}
            >
              <Flex gap={1} alignItems="center">
                <Tag>{sampleIds.length}</Tag>
                <Text>{t('productApproval.rejectPa')}</Text>
              </Flex>
            </MenuItem>
          </>
        )}
    </>
  );
}

export interface GeneralSampleModalsProps {
  onPerformedAction?: () => void;
}

export function useGeneralSampleModals({
  onPerformedAction = undefined,
}: GeneralSampleModalsProps = {}) {
  const {
    value: rejectValue,
    isOpen: rejectIsOpen,
    onClose: rejectOnClose,
    onOpen: rejectOnOpen,
  } = useValueDisclosure<number>();
  const {
    value: setLostValue,
    isOpen: setLostIsOpen,
    onClose: setLostOnClose,
    onOpen: setLostOnOpen,
  } = useValueDisclosure<number[]>();
  const {
    value: updateOwnerValue,
    isOpen: updateOwnerIsOpen,
    onClose: updateOwnerOnClose,
    onOpen: updateOwnerOnOpen,
  } = useValueDisclosure<number[]>();
  const {
    value: stopFollowValue,
    isOpen: stopFollowIsOpen,
    onClose: stopFollowOnClose,
    onOpen: stopFollowOnOpen,
  } = useValueDisclosure<number[]>();

  const modals = useMemo(() => {
    return (
      <>
        {rejectValue && (
          <>
            <RejectModal
              isOpen={rejectIsOpen}
              onClose={rejectOnClose}
              productApprovalId={rejectValue}
              onReject={() => {
                productApprovalEvents.productApprovalRejected.dispatch();
                if (onPerformedAction) onPerformedAction();
              }}
            />
          </>
        )}

        <SetSamplesAsLostModal
          isOpen={setLostIsOpen}
          onClose={setLostOnClose}
          sampleIds={setLostValue ?? []}
          onPerformed={() => {
            sampleEvents.onSamplesLost.dispatch();
            if (onPerformedAction) onPerformedAction();
          }}
        />
        <UpdateOwner
          isOpen={updateOwnerIsOpen}
          onClose={updateOwnerOnClose}
          sampleIds={updateOwnerValue ?? []}
          onPerformed={() => {
            sampleEvents.onSamplesUpdated.dispatch();
            if (onPerformedAction) onPerformedAction();
          }}
        />
        <StopFollowSamplesModal
          isOpen={stopFollowIsOpen}
          onClose={stopFollowOnClose}
          sampleIds={stopFollowValue ?? []}
          onPerformed={() => {
            sampleEvents.onSamplesUpdated.dispatch();
            if (onPerformedAction) onPerformedAction();
          }}
        />
      </>
    );
  }, [
    rejectIsOpen,
    rejectOnClose,
    rejectValue,
    setLostIsOpen,
    setLostOnClose,
    setLostValue,
    updateOwnerIsOpen,
    updateOwnerOnClose,
    updateOwnerValue,
    stopFollowIsOpen,
    stopFollowOnClose,
    stopFollowValue,
    onPerformedAction,
  ]);

  return {
    modals,
    setLostOnOpen,
    updateOwnerOnOpen,
    stopFollowOnOpen,
    rejectOnOpen,
  };
}
